/**
 * 投資保護機制
 * 
 * 確保區塊鏈交易成功後，投資記錄一定會被保存
 * 即使主流程失敗，也會通過備份機制確保數據不丟失
 */

import { supabase } from '@/config/supabase'
import { logger } from '@/utils/logger'

// 本地存儲鍵名
const PENDING_INVESTMENT_KEY = 'bentana_pending_investment'

/**
 * 待處理的投資記錄
 */
export interface PendingInvestment {
  investorAddress: string
  amount: string
  tokenAmount: string
  txHash: string
  stage: string
  referrerAddress?: string | null
  referralCode?: string | null
  referralReward?: string | null
  rootReferrerAddress?: string | null
  rootReferralReward?: string | null
  tokenReward?: string | null
  timestamp: number // 創建時間戳
  retryCount: number // 重試次數
}

/**
 * 在區塊鏈交易成功後，立即保存待處理投資到本地存儲
 * 這是第一道防線，確保即使後續步驟失敗，數據也不會丟失
 */
export function savePendingInvestment(investment: Omit<PendingInvestment, 'timestamp' | 'retryCount'>): void {
  try {
    const pending: PendingInvestment = {
      ...investment,
      timestamp: Date.now(),
      retryCount: 0
    }
    
    // 保存到 localStorage
    localStorage.setItem(PENDING_INVESTMENT_KEY, JSON.stringify(pending))
    logger.log('💾 [投資保護] 待處理投資已保存到本地存儲')
    logger.log(`   - txHash: ${investment.txHash}`)
    logger.log(`   - 金額: ${investment.amount} USDC`)
  } catch (error) {
    logger.error('❌ [投資保護] 保存待處理投資失敗:', error)
  }
}

/**
 * 清除待處理投資（投資記錄創建成功後調用）
 */
export function clearPendingInvestment(): void {
  try {
    localStorage.removeItem(PENDING_INVESTMENT_KEY)
    logger.log('✅ [投資保護] 待處理投資已清除')
  } catch (error) {
    logger.error('❌ [投資保護] 清除待處理投資失敗:', error)
  }
}

/**
 * 獲取待處理的投資記錄
 */
export function getPendingInvestment(): PendingInvestment | null {
  try {
    const data = localStorage.getItem(PENDING_INVESTMENT_KEY)
    if (!data) return null
    
    const pending = JSON.parse(data) as PendingInvestment
    
    // 檢查是否過期（超過 24 小時視為無效）
    const maxAge = 24 * 60 * 60 * 1000 // 24 小時
    if (Date.now() - pending.timestamp > maxAge) {
      logger.warn('⚠️ [投資保護] 待處理投資已過期，清除')
      clearPendingInvestment()
      return null
    }
    
    return pending
  } catch (error) {
    logger.error('❌ [投資保護] 獲取待處理投資失敗:', error)
    return null
  }
}

/**
 * 檢查並恢復待處理的投資
 * 應在應用啟動時調用
 */
export async function checkAndRecoverPendingInvestment(): Promise<boolean> {
  const pending = getPendingInvestment()
  
  if (!pending) {
    return false
  }
  
  logger.log('🔍 [投資保護] 發現待處理投資，開始檢查...')
  logger.log(`   - txHash: ${pending.txHash}`)
  logger.log(`   - 金額: ${pending.amount} USDC`)
  logger.log(`   - 重試次數: ${pending.retryCount}`)
  
  try {
    // 1. 檢查投資記錄是否已存在（可能是之前成功創建了）
    const { data: existingInvestment } = await supabase
      .from('investments')
      .select('id')
      .eq('tx_hash', pending.txHash.toLowerCase())
      .maybeSingle()
    
    if (existingInvestment) {
      logger.log('✅ [投資保護] 投資記錄已存在，無需恢復')
      clearPendingInvestment()
      return false
    }
    
    // 2. 嘗試創建投資記錄
    logger.log('🔄 [投資保護] 嘗試恢復投資記錄...')
    
    // 確保用戶存在
    await supabase
      .from('users')
      .upsert({
        wallet_address: pending.investorAddress.toLowerCase(),
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'wallet_address'
      })
    
    // 創建投資記錄
    const insertData: any = {
      investor_address: pending.investorAddress.toLowerCase(),
      amount: pending.amount,
      token_amount: pending.tokenAmount,
      tx_hash: pending.txHash.toLowerCase(),
      stage: pending.stage,
      referrer_address: pending.referrerAddress?.toLowerCase() || null,
      referral_code: pending.referralCode || null,
      referral_reward: pending.referralReward || null,
      token_reward: pending.tokenReward || null,
      status: 'confirmed'
    }

    // ⭐ 動態添加級差獎勵欄位，增加容錯性
    if (pending.rootReferrerAddress) {
      insertData.root_referrer_address = pending.rootReferrerAddress.toLowerCase()
    }
    if (pending.rootReferralReward) {
      insertData.root_referral_reward = pending.rootReferralReward
    }

    const { data: newInvestment, error } = await supabase
      .from('investments')
      .insert(insertData)
      .select()
      .single()
    
    if (error) {
      // 如果是唯一約束衝突，說明記錄已存在
      if (error.code === '23505') {
        logger.log('✅ [投資保護] 投資記錄已存在（唯一約束）')
        clearPendingInvestment()
        return false
      }

      // ⭐ 特殊處理：如果是欄位不存在錯誤，嘗試降級恢復
      if (error.message.includes('root_referral_reward') || error.message.includes('root_referrer_address')) {
        logger.warn('⚠️ [投資保護] 恢復失敗（欄位不存在），嘗試降級恢復...')
        
        const fallbackData = { ...insertData }
        delete fallbackData.root_referrer_address
        delete fallbackData.root_referral_reward
        
        const { data: fallbackResult, error: fallbackError } = await supabase
          .from('investments')
          .insert(fallbackData)
          .select()
          .single()
          
        if (!fallbackError) {
          logger.log('✅ [投資保護] 降級恢復成功!')
          clearPendingInvestment()
          return true
        }
        logger.error('❌ [投資保護] 降級恢復也失敗了:', fallbackError)
      }
      
      // 其他錯誤，增加重試計數
      pending.retryCount++
      localStorage.setItem(PENDING_INVESTMENT_KEY, JSON.stringify(pending))
      
      logger.error('❌ [投資保護] 恢復投資記錄失敗:', error)
      logger.error(`   - 錯誤代碼: ${error.code}`)
      logger.error(`   - 錯誤信息: ${error.message}`)
      
      // 如果重試次數過多，記錄到錯誤日誌表
      if (pending.retryCount >= 3) {
        await logFailedInvestment(pending, error.message)
      }
      
      return false
    }
    
    logger.log('✅ [投資保護] 投資記錄恢復成功!')
    logger.log(`   - ID: ${newInvestment.id}`)
    clearPendingInvestment()
    
    return true
  } catch (error: any) {
    logger.error('❌ [投資保護] 恢復過程發生異常:', error)
    
    // 增加重試計數
    pending.retryCount++
    localStorage.setItem(PENDING_INVESTMENT_KEY, JSON.stringify(pending))
    
    return false
  }
}

/**
 * 記錄失敗的投資到錯誤日誌表（用於人工處理）
 */
async function logFailedInvestment(pending: PendingInvestment, errorMessage: string): Promise<void> {
  try {
    await supabase
      .from('error_logs')
      .insert({
        error_type: 'investment_creation_failed',
        error_message: `投資記錄創建失敗，需要人工處理`,
        user_address: pending.investorAddress,
        diagnostic_data: {
          txHash: pending.txHash,
          amount: pending.amount,
          tokenAmount: pending.tokenAmount,
          stage: pending.stage,
          referrerAddress: pending.referrerAddress,
          referralCode: pending.referralCode,
          referralReward: pending.referralReward,
          rootReferrerAddress: pending.rootReferrerAddress,
          rootReferralReward: pending.rootReferralReward,
          tokenReward: pending.tokenReward,
          timestamp: pending.timestamp,
          retryCount: pending.retryCount,
          lastError: errorMessage
        },
        status: 'unresolved'
      })
    
    logger.log('📝 [投資保護] 失敗投資已記錄到錯誤日誌表')
  } catch (error) {
    logger.error('❌ [投資保護] 記錄錯誤日誌失敗:', error)
  }
}

/**
 * 檢查鏈上交易是否成功（用於驗證）
 * 注意：這需要 RPC 節點支持
 */
export async function verifyOnChainTransaction(
  txHash: string,
  expectedAmount: string,
  expectedRecipient: string
): Promise<{
  verified: boolean
  error?: string
}> {
  // 這個功能需要連接到區塊鏈 RPC 節點
  // 目前返回基本驗證結果
  logger.log(`🔍 [投資保護] 驗證鏈上交易: ${txHash}`)
  
  // TODO: 實現實際的鏈上驗證
  // 可以通過 BscScan API 或直接查詢 RPC 節點
  
  return {
    verified: true // 暫時假設已驗證
  }
}
